
import { useState } from 'react';
import { CalendarEvent } from '../types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useMultiSelect = () => {
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState<Record<string, boolean>>({});

  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode((prev) => !prev);
    if (!isMultiSelectMode) {
      setSelectedEvents({});
    }
  };

  const toggleEventSelection = (eventId: string) => {
    setSelectedEvents((prev) => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const clearSelection = () => {
    setSelectedEvents({});
  };

  const getSelectedEvents = (allEvents: CalendarEvent[]) => {
    return allEvents.filter((event) => selectedEvents[event.id]);
  };

  const selectedCount = () => {
    return Object.values(selectedEvents).filter(Boolean).length;
  };

  const selectAll = (events: CalendarEvent[]) => {
    const newSelected: Record<string, boolean> = {};
    events.forEach(event => {
      newSelected[event.id] = true;
    });
    setSelectedEvents(newSelected);
  };

  const deselectAll = () => {
    setSelectedEvents({});
  };

  const isSelected = (eventId: string) => {
    return !!selectedEvents[eventId];
  };

  const deleteSelectedEvents = async (events: CalendarEvent[]) => {
    try {
      const selectedIds = Object.entries(selectedEvents)
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => id);

      if (selectedIds.length === 0) {
        toast.info("No events selected for deletion");
        return;
      }

      const { error, count } = await supabase
        .from('team_schedules')
        .delete({ count: 'exact' })
        .in('id', selectedIds);
      
      if (error) throw error;

      toast.success(`Successfully deleted ${count || selectedIds.length} events`);
      clearSelection();
      
      // Dispatch a custom event to trigger a refetch of the schedules
      document.dispatchEvent(new CustomEvent('refetch-schedules'));
      
      return true;
    } catch (error) {
      console.error('Error deleting selected events:', error);
      toast.error('Failed to delete selected events');
      return false;
    }
  };

  // Convert the Record<string, boolean> to Set<string> for components that expect a Set
  const getSelectedEventsAsSet = (): Set<string> => {
    return new Set(
      Object.entries(selectedEvents)
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => id)
    );
  };

  return {
    isMultiSelectMode,
    toggleMultiSelectMode,
    selectedEvents,
    selectedEventsSet: getSelectedEventsAsSet(), // Add a Set version
    toggleEventSelection,
    clearSelection,
    getSelectedEvents,
    selectedCount,
    selectAll,
    deselectAll,
    isSelected,
    deleteSelectedEvents
  };
};
